import type React from 'react';

// lodash
import _ from 'lodash';

import { downloadAWSDoc } from '../../accounts/AccountService';
import { downloadDocument } from '../../api/DocumentsService';

import { saveBlobFile } from '../../utils/appUtils';
import useNotifications from '../../_lib/notifications/useNotifications';

interface ChildrenArgs {
  handleDocumentDownload: () => unknown;
  [key: string]: unknown;
}

export interface DocumentDownloaderProps {
  id?: string;
  name: string;
  type: string;
  onDownload?: () => Promise<any>;
  onError?: () => void;
  children: (props: ChildrenArgs) => React.JSX.Element | null;
}

export const DocumentDownloader: React.FC<DocumentDownloaderProps> = ({
  id,
  name,
  type,
  children,
  onDownload,
  onError = null,
  ...props
}) => {
  const notifications = useNotifications();
  type UrlsDto = { urls: string[]; errorMessage?: string };
  function isUrlsDto(value: unknown): value is UrlsDto {
    return (
      !!value &&
      typeof value === 'object' &&
      'urls' in (value as Record<string, unknown>) &&
      Array.isArray((value as Record<string, unknown>).urls)
    );
  }

  async function handleDocumentDownload() {
    const handleDownload =
      typeof onDownload === 'function'
        ? onDownload
        : () => downloadDocument({ docId: id });

    const resp = await handleDownload().catch(() => undefined);
    const data = _.get(resp, 'data') as unknown;
    const urls: string[] =
      typeof data === 'string' ? [data] : isUrlsDto(data) ? data.urls : [];

    // Surface optional errorMessage from UrlsDto
    if (isUrlsDto(data) && data.errorMessage)
      notifications.warning(data.errorMessage);

    if (!urls.length) {
      if (typeof onError === 'function') {
        return onError();
      }
      return notifications.error('Download url not available');
    }

    try {
      notifications.show({
        type: 'info',
        title: 'Document download started, file will be available shortly',
        position: 'top-right',
      });

      // Download all urls; keep original name for first, append index for others
      for (let i = 0; i < urls.length; i++) {
        const u = urls[i];
        if (!u) continue;
        const awsResp = await downloadAWSDoc(u);
        const suffix = i === 0 ? '' : `_${i + 1}`;
        saveBlobFile(awsResp, `${name}${suffix}`, `.${type}`);
      }
    } catch (error) {
      notifications.error('Unable to download document');
    }
  }

  return children({ ...props, handleDocumentDownload });
};
